import React, { useEffect, useState } from 'react';
import { ReactFlow, useNodesState, useNodesInitialized, ReactFlowProvider, useReactFlow } from '@xyflow/react';
import { nodeTypes } from './components/nodes/CustomNode';
import { createNodesFromHierarchy } from './utils/graphDataUtils';
import PageLoader from 'components/loaders/PageLoader';
import { withTranslation } from 'react-i18next';
import styles from './graph.scss';

/**
 * Calculates the area of the layout by calculating the area of the bounding box around all nodes.
 */
export const calculateLayoutArea = layout => {
  if (!layout || layout.length === 0) {
    return 0; // No area if layout is empty or invalid
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  layout.forEach(node => {
    // Use provided dimensions, fallback to defaults if necessary (like in MeasurementFlow)
    const nodeWidth = node.width || 150;
    const nodeHeight = node.height || 50;

    const nodeMinX = node.x;
    const nodeMaxX = node.x + nodeWidth;
    const nodeMinY = node.y;
    const nodeMaxY = node.y + nodeHeight;

    minX = Math.min(minX, nodeMinX);
    maxX = Math.max(maxX, nodeMaxX);
    minY = Math.min(minY, nodeMinY);
    maxY = Math.max(maxY, nodeMaxY);
  });

  // If no valid nodes were found (e.g., all had non-finite coordinates)
  if (!isFinite(minX) || !isFinite(maxX) || !isFinite(minY) || !isFinite(maxY)) {
    return 0;
  }

  const width = maxX - minX;
  const height = maxY - minY;

  return width * height;
};

/**
 * Renders a very light react flow component with our styled nodes in order to get accurate width/height measurements for the layouting.
 */
const MeasurementFlow = ({ graph, onMeasurementComplete }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const reactFlowInstance = useReactFlow();
  const nodesInitialized = useNodesInitialized();

  // Create initial nodes for measurement
  useEffect(() => {
    if (graph.nodes && graph.flowNodeStructure && !nodesInitialized && !graph.isLoadingGraph) {
      const initialNodes = createNodesFromHierarchy(
        graph.flowNodeStructure,
        graph.nodes,
        [],
        () => {}, // empty callbacks
        () => {},
        false
      );
      setNodes(initialNodes);
    }
  }, [graph.nodes, graph.flowNodeStructure, nodesInitialized, graph.isLoadingGraph]);

  // Get measurements once nodes are initialized
  useEffect(() => {
    if (nodesInitialized && nodes.length > 0 && graph.needsToFetchGraphLayout && !graph.isLoadingGraphLayout) {
      const nodeMeasurements = reactFlowInstance.getNodes().map(node => {
        const { width, height } = node.measured || { width: 150, height: 50 };
        return { id: node.id, width, height };
      });

      onMeasurementComplete(nodeMeasurements);
    }
  }, [
    nodesInitialized,
    graph.needsToFetchGraphLayout,
    graph.isLoadingGraphLayout,
    nodes.length,
    onMeasurementComplete
  ]);

  return (
    <ReactFlow
      nodes={nodes}
      onNodesChange={onNodesChange}
      proOptions={{ hideAttribution: true }}
      nodeTypes={nodeTypes}
      fitView={false} // need to have it zoomed in completely to get correct measurements
    />
  );
};

const KnowledgeGraphLayoutManager = ({ children, graph, dispatch, fetchGraphLayout, t, style }) => {
  const [isMeasuring, setIsMeasuring] = useState(false);

  useEffect(() => {
    if (graph.needsToFetchGraphLayout && !graph.isLoadingGraphLayout && !isMeasuring) {
      setIsMeasuring(true);
    }
  }, [graph.needsToFetchGraphLayout, graph.isLoadingGraphLayout, isMeasuring]);

  const handleMeasurementComplete = measurements => {
    dispatch(fetchGraphLayout(measurements)).finally(() => {
      setIsMeasuring(false);
    });
  };

  if (isMeasuring) {
    return (
      <>
        <ReactFlowProvider>
          <div style={{ height: '100%', width: '100%' }}>
            <MeasurementFlow graph={graph} onMeasurementComplete={handleMeasurementComplete} />
          </div>
        </ReactFlowProvider>
        {/*Add a background so the page loader certainly hides the measurement flow above which we can't seem to hide w/o interfering with measurement */}
        <div className={styles.measurementOverlay} style={style}>
          <PageLoader message={t('knowledge-graph.fetching-layout')} />
        </div>
      </>
    );
  }

  if (!graph || graph.isLoadingGraph) {
    return <PageLoader message={t('knowledge-graph.loading')} />;
  }

  if (graph.isLoadingGraphLayout) {
    return <PageLoader message={t('knowledge-graph.fetching-layout')} />;
  }

  // Actually render the corresponding graph
  return children;
};

export default withTranslation('veezoo')(KnowledgeGraphLayoutManager);
